import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { DashboardApi, type DiffDelta, HttpError } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

const CONFORMANCE_OPTIONS = [
  'Supports',
  'Partially Supports',
  'Does Not Support',
  'Not Applicable',
  'Not Evaluated',
] as const;

interface ScanListItem {
  id: string;
  productName: string;
  productVersion: string;
  source: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | null;
  startedAt: string;
  completedAt: string | null;
  pageCount: number | null;
  buildRef: string | null;
}

type Conformance =
  | 'Supports'
  | 'Partially Supports'
  | 'Does Not Support'
  | 'Not Applicable'
  | 'Not Evaluated';

interface AggregatedFinding {
  criterion: string;
  conformance: Conformance;
  remarks: string;
  evidence: string[];
  confidence: number;
  pageCount: number;
  contributingCapabilities: string[];
  override?: {
    id: string;
    originalConformance: string;
    reason: string;
    overriddenByUserId: string;
    overriddenAt: string;
  };
}

const STATUS_COLOR: Record<string, string> = {
  pending: 'pill-muted',
  running: 'pill-info',
  completed: 'pill-supports',
  failed: 'pill-fails',
};

const CONFORMANCE_PILL: Record<Conformance, string> = {
  Supports: 'pill-supports',
  'Partially Supports': 'pill-partial',
  'Does Not Support': 'pill-fails',
  'Not Applicable': 'pill-muted',
  'Not Evaluated': 'pill-muted',
};

export function Scans({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [scans, setScans] = useState<ScanListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
  const [productFilter, setProductFilter] = useState<string>('');

  const refresh = async () => {
    try {
      const r = await api.listScans(org.id);
      setScans(r.scans);
      setError(null);
    } catch (e) {
      const detail = e instanceof HttpError ? e.message : String(e);
      setError(
        `Couldn't load scans — refresh to try again, and if this keeps happening contact support@wholisphere.ai. (${detail})`,
      );
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is stable
  useEffect(() => {
    void refresh();
  }, [org.id]);

  const products = useMemo(() => {
    if (!scans) return [];
    return [...new Set(scans.map((s) => s.productName))].sort();
  }, [scans]);

  const filtered = useMemo(() => {
    if (!scans) return [];
    if (!productFilter) return scans;
    return scans.filter((s) => s.productName === productFilter);
  }, [scans, productFilter]);

  return (
    <>
      <div class="page-header">
        <h1>Scans</h1>
        <p>Every WCAG scan submitted via the runner, the agent, or the API.</p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Recent scans</h2>
          {products.length > 1 && (
            <label class="small muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              Product:{' '}
              <select
                value={productFilter}
                onChange={(e) => setProductFilter((e.target as HTMLSelectElement).value)}
              >
                <option value="">All</option>
                {products.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {scans === null && (
          <p class="small muted">
            <output>Loading…</output>
          </p>
        )}
        {scans !== null && filtered.length === 0 && (
          <div class="empty-state" style={{ padding: '24px 8px' }}>
            <p>
              No scans yet. To scan a site by URL right now, use{' '}
              <button
                type="button"
                class="link-button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent('whs-nav', { detail: 'hosted-scans' }))
                }
              >
                Hosted scans
              </button>
              . You can also run one via the GitHub Action, the CLI, or the API.
            </p>
          </div>
        )}
        {filtered.length > 0 && (
          <table class="data-table" style={{ width: '100%', marginTop: 16 }}>
            <thead>
              <tr>
                <th>Scan</th>
                <th>Product</th>
                <th>Status</th>
                <th class="num">Pages</th>
                <th>Started</th>
                <th>Build ref</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td>
                    <code>{s.id.slice(0, 12)}…</code>
                  </td>
                  <td>
                    {s.productName}
                    <br />
                    <span class="small muted">{s.productVersion}</span>
                  </td>
                  <td>
                    <span class={`pill ${STATUS_COLOR[s.status ?? 'pending'] ?? 'pill-muted'}`}>
                      {s.status ?? 'unknown'}
                    </span>
                  </td>
                  <td class="num">{s.pageCount ?? 0}</td>
                  <td class="small muted">{new Date(s.startedAt).toLocaleString()}</td>
                  <td class="small">{s.buildRef ? <code>{s.buildRef.slice(0, 8)}</code> : '—'}</td>
                  <td>
                    <button
                      type="button"
                      class="link-button"
                      onClick={() => setSelectedScanId(s.id)}
                    >
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedScanId && (
        <ScanDetail
          orgId={org.id}
          scanId={selectedScanId}
          allScans={scans ?? []}
          onClose={() => setSelectedScanId(null)}
        />
      )}
    </>
  );
}

function ScanDetail({
  orgId,
  scanId,
  allScans,
  onClose,
}: {
  orgId: string;
  scanId: string;
  allScans: ScanListItem[];
  onClose: () => void;
}): preact.ComponentChildren {
  const [detail, setDetail] = useState<{
    scan: ScanListItem;
    aggregated: AggregatedFinding[];
    rawFindingCount: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [overridingCriterion, setOverridingCriterion] = useState<string | null>(null);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [diffAgainst, setDiffAgainst] = useState<string>('');
  const [diff, setDiff] = useState<{
    counts: {
      regressed: number;
      improved: number;
      unchanged: number;
      introduced: number;
      removed: number;
      total: number;
    };
    hasRegressions: boolean;
    regressed: DiffDelta[];
    improved: DiffDelta[];
    introduced: DiffDelta[];
    removed: DiffDelta[];
  } | null>(null);
  const [diffing, setDiffing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Dialog focus management: move focus into the panel on open, return it to
  // the triggering "View →" button on close. (Same pattern as the widget panel.)
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => {
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  useEffect(() => {
    setDetail(null);
    setDiff(null);
    setDiffAgainst('');
    api
      .scanDetail(orgId, scanId)
      .then((d) => setDetail(d as never))
      .catch((e) => setError(e instanceof HttpError ? e.message : String(e)));
  }, [orgId, scanId]);

  const compareTo = async (otherId: string) => {
    if (!otherId) {
      setDiff(null);
      return;
    }
    setDiffing(true);
    setDiffAgainst(otherId);
    try {
      const r = await api.scanDiff(orgId, scanId, otherId);
      setDiff(r);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setDiffing(false);
    }
  };

  const reloadDetail = async (): Promise<void> => {
    try {
      const d = await api.scanDetail(orgId, scanId);
      setDetail(d as never);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  const submitOverride = async (
    criterion: string,
    originalConformance: AggregatedFinding['conformance'],
    newConformance: AggregatedFinding['conformance'],
    reason: string,
  ): Promise<void> => {
    setOverrideBusy(true);
    setError(null);
    try {
      await api.overrideFinding(orgId, scanId, {
        criterion,
        conformance: newConformance,
        originalConformance,
        reason,
      });
      setOverridingCriterion(null);
      await reloadDetail();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setOverrideBusy(false);
    }
  };

  const revertOverride = async (criterion: string): Promise<void> => {
    if (!confirm(`Revert override on ${criterion}? The aggregator's verdict will return.`)) return;
    setOverrideBusy(true);
    setError(null);
    try {
      await api.revertFindingOverride(orgId, scanId, criterion);
      await reloadDetail();
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    } finally {
      setOverrideBusy(false);
    }
  };

  const otherScans = allScans.filter(
    (s) => s.id !== scanId && detail?.scan && s.productName === detail.scan.productName,
  );

  return (
    <div
      class="card"
      style={{ marginTop: 16 }}
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ marginTop: 0 }}>Scan detail</h2>
          <code class="small">{scanId}</code>
        </div>
        <button type="button" class="link-button" onClick={onClose}>
          Close ✕
        </button>
      </div>
      {error && <Banner variant="warn">{error}</Banner>}
      {detail === null && !error && (
        <p class="small muted">
          <output>Loading scan detail…</output>
        </p>
      )}
      {detail && (
        <>
          <dl
            style={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '4px 16px',
              margin: '12px 0',
            }}
          >
            <dt class="small muted">Product</dt>
            <dd>
              {detail.scan.productName} {detail.scan.productVersion}
            </dd>
            <dt class="small muted">Status</dt>
            <dd>
              <span class={`pill ${STATUS_COLOR[detail.scan.status ?? 'pending'] ?? 'pill-muted'}`}>
                {detail.scan.status ?? 'unknown'}
              </span>
            </dd>
            <dt class="small muted">Pages</dt>
            <dd>{detail.scan.pageCount ?? 0}</dd>
            <dt class="small muted">Findings</dt>
            <dd>
              {detail.rawFindingCount} raw → {detail.aggregated.length} per-criterion verdicts
            </dd>
            {detail.scan.completedAt && (
              <>
                <dt class="small muted">Completed</dt>
                <dd>{new Date(detail.scan.completedAt).toLocaleString()}</dd>
              </>
            )}
            {detail.scan.buildRef && (
              <>
                <dt class="small muted">Build ref</dt>
                <dd>
                  <code>{detail.scan.buildRef}</code>
                </dd>
              </>
            )}
          </dl>

          {otherScans.length > 0 && (
            <div class="field" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label htmlFor="diff-against">Compare to:</label>
              <select
                id="diff-against"
                value={diffAgainst}
                onChange={(e) => void compareTo((e.target as HTMLSelectElement).value)}
              >
                <option value="">— pick a scan —</option>
                {otherScans.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.productVersion} · {new Date(s.startedAt).toLocaleDateString()} (
                    {s.id.slice(0, 8)})
                  </option>
                ))}
              </select>
              {diffing && <output class="small muted">Diffing…</output>}
            </div>
          )}

          {diff && (
            <div class="card" style={{ marginTop: 12, background: 'var(--bg-tint, #f5f7fb)' }}>
              <h3 style={{ marginTop: 0 }}>
                Diff vs <code>{diffAgainst.slice(0, 12)}…</code>
                {diff.hasRegressions && (
                  <span class="pill pill-fails" style={{ marginLeft: 8 }}>
                    regressions
                  </span>
                )}
              </h3>
              <p class="small muted">
                {diff.counts.regressed} regressed · {diff.counts.improved} improved ·{' '}
                {diff.counts.introduced} new · {diff.counts.removed} removed ·{' '}
                {diff.counts.unchanged} unchanged
              </p>
              {diff.regressed.length > 0 && (
                <DiffSection title="Regressed" deltas={diff.regressed} variant="fails" />
              )}
              {diff.improved.length > 0 && (
                <DiffSection title="Improved" deltas={diff.improved} variant="supports" />
              )}
              {diff.introduced.length > 0 && (
                <DiffSection title="Newly evaluated" deltas={diff.introduced} variant="info" />
              )}
              {diff.removed.length > 0 && (
                <DiffSection title="No longer evaluated" deltas={diff.removed} variant="muted" />
              )}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 18,
            }}
          >
            <h3 style={{ margin: 0 }}>
              Per-criterion verdicts
              {(() => {
                const reviewCount = detail.aggregated.filter((a) => a.confidence < 0.6).length;
                if (reviewCount === 0) return null;
                return (
                  <span class="pill pill-partial" style={{ marginLeft: 8 }}>
                    {reviewCount} need{reviewCount === 1 ? 's' : ''} review
                  </span>
                );
              })()}
            </h3>
            <label class="small muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={needsReviewOnly}
                onChange={(e) => setNeedsReviewOnly((e.target as HTMLInputElement).checked)}
              />
              Show only needs-review (confidence &lt; 60%)
            </label>
          </div>
          {detail.aggregated.length === 0 && (
            <p class="small muted">No findings yet for this scan.</p>
          )}
          {detail.aggregated.length > 0 &&
            (() => {
              const filtered = needsReviewOnly
                ? detail.aggregated.filter((a) => a.confidence < 0.6)
                : detail.aggregated;
              if (needsReviewOnly && filtered.length === 0) {
                return (
                  <p class="small muted" style={{ marginTop: 12 }}>
                    Nothing below the 60% confidence threshold — every verdict is high-confidence.
                  </p>
                );
              }
              return (
                <table class="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Criterion</th>
                      <th>Conformance</th>
                      <th>Remarks</th>
                      <th class="num">Pages</th>
                      <th class="num">Conf.</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => {
                      const lowConfidence = a.confidence < 0.6;
                      const overridden = !!a.override;
                      const rowStyle: Record<string, string> = {};
                      if (overridden)
                        rowStyle['borderLeft'] = '3px solid var(--color-accent, #0b5fff)';
                      else if (lowConfidence)
                        rowStyle['borderLeft'] = '3px solid var(--color-partial, #b86e00)';
                      return (
                        <tr key={a.criterion} style={rowStyle}>
                          <td>
                            <strong>{a.criterion}</strong>
                          </td>
                          <td>
                            <span class={`pill ${CONFORMANCE_PILL[a.conformance]}`}>
                              {a.conformance}
                            </span>
                            {overridden && a.override && (
                              <div class="small muted" style={{ marginTop: 4 }}>
                                <strong>overridden</strong> — was{' '}
                                <code>{a.override.originalConformance}</code>
                              </div>
                            )}
                          </td>
                          <td class="small">
                            {a.remarks}
                            {a.evidence.length > 0 && (
                              <details style={{ marginTop: 4 }}>
                                <summary class="small muted">
                                  {a.evidence.length} evidence link(s)
                                </summary>
                                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12 }}>
                                  {a.evidence.map((e) => (
                                    <li key={e}>
                                      <code>{e}</code>
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            )}
                            {lowConfidence && !overridden && (
                              <div class="small muted" style={{ marginTop: 4 }}>
                                ⚠ low confidence — recommended for human review before approval
                              </div>
                            )}
                            {overridden && a.override && (
                              <details style={{ marginTop: 4 }}>
                                <summary class="small muted">Override reason</summary>
                                <div class="small" style={{ padding: '6px 0' }}>
                                  "{a.override.reason}"
                                  <br />
                                  <span class="muted">
                                    by {a.override.overriddenByUserId.slice(0, 8)} on{' '}
                                    {new Date(a.override.overriddenAt).toLocaleString()}
                                  </span>
                                </div>
                              </details>
                            )}
                          </td>
                          <td class="num">{a.pageCount}</td>
                          <td
                            class="num"
                            style={
                              lowConfidence && !overridden
                                ? { color: 'var(--color-partial, #b86e00)', fontWeight: 600 }
                                : {}
                            }
                          >
                            {(a.confidence * 100).toFixed(0)}%
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {overridden ? (
                              <button
                                type="button"
                                class="link-button"
                                disabled={overrideBusy}
                                onClick={() => void revertOverride(a.criterion)}
                              >
                                Revert
                              </button>
                            ) : (
                              <button
                                type="button"
                                class="link-button"
                                disabled={overrideBusy}
                                onClick={() => setOverridingCriterion(a.criterion)}
                              >
                                Override
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}

          {overridingCriterion &&
            (() => {
              const target = detail.aggregated.find((a) => a.criterion === overridingCriterion);
              if (!target) return null;
              return (
                <OverrideDialog
                  criterion={target.criterion}
                  originalConformance={target.conformance}
                  busy={overrideBusy}
                  onCancel={() => setOverridingCriterion(null)}
                  onSubmit={(newConformance, reason) =>
                    submitOverride(target.criterion, target.conformance, newConformance, reason)
                  }
                />
              );
            })()}
        </>
      )}
    </div>
  );
}

function OverrideDialog({
  criterion,
  originalConformance,
  busy,
  onCancel,
  onSubmit,
}: {
  criterion: string;
  originalConformance: AggregatedFinding['conformance'];
  busy: boolean;
  onCancel: () => void;
  onSubmit: (newConformance: AggregatedFinding['conformance'], reason: string) => Promise<void>;
}): preact.ComponentChildren {
  const [conformance, setConformance] =
    useState<AggregatedFinding['conformance']>(originalConformance);
  const [reason, setReason] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on open; return it to the "Override" button on close.
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => {
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  const submit = async (): Promise<void> => {
    if (reason.trim().length === 0) return;
    await onSubmit(conformance, reason.trim());
  };
  return (
    <div
      class="card"
      style={{ marginTop: 12, borderLeft: '3px solid var(--color-accent, #0b5fff)' }}
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // Close only this dialog, not the enclosing scan-detail panel.
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <h3 style={{ marginTop: 0 }}>Override {criterion}</h3>
      <p class="small muted">
        Original verdict: <code>{originalConformance}</code>. The original is preserved on the
        override row + audit log; the new verdict appears in the VPAT and on every dashboard read.
      </p>
      <div class="field">
        <label htmlFor="override-conformance">New conformance</label>
        <select
          id="override-conformance"
          value={conformance}
          onChange={(e) =>
            setConformance(
              (e.target as HTMLSelectElement).value as AggregatedFinding['conformance'],
            )
          }
        >
          {CONFORMANCE_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      <div class="field">
        <label htmlFor="override-reason">Reason (required, audit-logged)</label>
        <textarea
          id="override-reason"
          rows={3}
          value={reason}
          onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
          placeholder="e.g. Reviewed manually — agent vision LLM was wrong; CTAs do meet 4.5:1 in production."
        />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          disabled={busy || reason.trim().length === 0}
          onClick={() => void submit()}
        >
          {busy ? 'Saving…' : 'Save override'}
        </button>
        <button type="button" class="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function DiffSection({
  title,
  deltas,
  variant,
}: {
  title: string;
  deltas: DiffDelta[];
  variant: 'fails' | 'supports' | 'info' | 'muted';
}): preact.ComponentChildren {
  const pillClass = `pill pill-${variant === 'info' ? 'muted' : variant}`;
  return (
    <div style={{ marginTop: 10 }}>
      <h4 style={{ margin: '8px 0' }}>
        <span class={pillClass}>{title}</span> {deltas.length}
      </h4>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {deltas.map((d) => (
          <li key={d.criterion} class="small">
            <strong>{d.criterion}</strong> — {d.summary}
          </li>
        ))}
      </ul>
    </div>
  );
}
